import axios from 'axios';
import { toastr } from 'react-redux-toastr';
import { createAction } from 'redux-act';
import { url } from 'utils/url';
import { errorMsg } from 'utils';

export const TEMPLATE_FETCH_DATA_INIT = createAction(
  'TEMPLATE_FETCH_DATA_INIT'
);
export const TEMPLATE_FETCH_DATA_SUCCESS = createAction(
  'TEMPLATE_FETCH_DATA_SUCCESS'
);
export const TEMPLATE_FETCH_DATA_FAIL = createAction(
  'TEMPLATE_FETCH_DATA_FAIL'
);

export const TEMPLATE_SEARCH_INIT = createAction('TEMPLATE_SEARCH_INIT');
export const TEMPLATE_SEARCH_SUCCESS = createAction('TEMPLATE_SEARCH_SUCCESS');
export const TEMPLATE_SEARCH_FAIL = createAction('TEMPLATE_SEARCH_FAIL');

export const TEMPLATE_CREATE_INIT = createAction('TEMPLATE_CREATE_INIT');
export const TEMPLATE_CREATE_SUCCESS = createAction('TEMPLATE_CREATE_SUCCESS');
export const TEMPLATE_CREATE_FAIL = createAction('TEMPLATE_CREATE_FAIL');

export const TEMPLATE_UPDATE_INIT = createAction('TEMPLATE_UPDATE_INIT');
export const TEMPLATE_UPDATE_SUCCESS = createAction('TEMPLATE_UPDATE_SUCCESS');
export const TEMPLATE_UPDATE_FAIL = createAction('TEMPLATE_UPDATE_FAIL');

export const TEMPLATE_DELETE_INIT = createAction('TEMPLATE_DELETE_INIT');
export const TEMPLATE_DELETE_SUCCESS = createAction('TEMPLATE_DELETE_SUCCESS');
export const TEMPLATE_DELETE_FAIL = createAction('TEMPLATE_DELETE_FAIL');

export const TEMPLATE_FILTER_BY_SCREEN_INIT = createAction(
  'TEMPLATE_FILTER_BY_SCREEN_INIT'
);
export const TEMPLATE_FILTER_BY_SCREEN_SUCCESS = createAction(
  'TEMPLATE_FILTER_BY_SCREEN_SUCCESS'
);
export const TEMPLATE_FILTER_BY_SCREEN_FAIL = createAction(
  'TEMPLATE_FILTER_BY_SCREEN_FAIL'
);

export const TEMPLATE_CLEAR_DATA = createAction('TEMPLATE_CLEAR_DATA');
export const TEMPLATE_CLEAR_DATA_LOGOUT = createAction(
  'TEMPLATE_CLEAR_DATA_LOGOUT'
);
export const TEMPLATE_CLEAN_UP = createAction('TEMPLATE_CLEAN_UP');
export const SET_TEMPLATE = createAction('SET_TEMPLATE');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
});

export const fetchTemplates = ({ siteId, type = 'published' } = {}) => {
  return async (dispatch, getState) => {
    dispatch(TEMPLATE_FETCH_DATA_INIT());
    let templates;
    try {
      const response = await axios.post(
        `${url}/template/${siteId}`,
        { type },
        { headers: authHeaders() }
      );
      templates = response.data.data;
    } catch (error) {
      toastr.error('Error Fetching Templates', `${errorMsg(dispatch, error)}`);
      return dispatch(TEMPLATE_FETCH_DATA_FAIL({ error }));
    }

    return dispatch(
      TEMPLATE_FETCH_DATA_SUCCESS({
        list: templates,
        template: getState().template.template,
      })
    );
  };
};

export const fetchTemplateById = ({ siteId, id }) => {
  return async (dispatch, getState) => {
    dispatch(TEMPLATE_FETCH_DATA_INIT());
    let templateData;
    try {
      const response = await axios.get(`${url}/template/${siteId}/${id}`, {
        headers: authHeaders(),
      });
      templateData = response.data.data;
    } catch (error) {
      toastr.error('Error Fetching Template', `${errorMsg(dispatch, error)}`);
      return dispatch(TEMPLATE_FETCH_DATA_FAIL({ error }));
    }
    return dispatch(
      TEMPLATE_FETCH_DATA_SUCCESS({
        list: getState().template.list,
        template: templateData,
      })
    );
  };
};

export const searchTemplates = ({ siteId, search, type }) => {
  return async (dispatch) => {
    dispatch(TEMPLATE_SEARCH_INIT());
    let searchResults;
    try {
      const response = await axios.get(`${url}/template/${siteId}/search`, {
        headers: authHeaders(),
        params: { search, type },
      });
      searchResults = response.data.data;
    } catch (error) {
      toastr.error('', `${errorMsg(dispatch, error)}`);
      return dispatch(TEMPLATE_SEARCH_FAIL({ error }));
    }
    return dispatch(TEMPLATE_SEARCH_SUCCESS({ searchResults }));
  };
};

export const filterTemplatesByScreenId = ({ siteId, screenId }) => {
  return async (dispatch) => {
    dispatch(TEMPLATE_FILTER_BY_SCREEN_INIT());
    let filteredResults;
    try {
      const response = await axios.get(
        `${url}/template/${siteId}/screen/${screenId}`,
        { headers: authHeaders() }
      );
      filteredResults = response.data.data;
    } catch (error) {
      toastr.error(
        'Error Filtering Templates',
        `${errorMsg(dispatch, error)}`
      );
      return dispatch(TEMPLATE_FILTER_BY_SCREEN_FAIL({ error }));
    }
    return dispatch(TEMPLATE_FILTER_BY_SCREEN_SUCCESS({ filteredResults }));
  };
};

export const createTemplate = (templateData, cb) => {
  return async (dispatch) => {
    dispatch(TEMPLATE_CREATE_INIT());
    try {
      const payload = {
        name: templateData.name,
        description: templateData.description,
        siteId: templateData.siteId,
        screenIds: templateData.screenIds || [],
        start: templateData.start,
        end: templateData.end,
        totalDuration: templateData.totalDuration || 60,
        isArchived: templateData.isArchived || false,
        slots: templateData.slots || [],
      };
      const response = await axios.post(`${url}/template/add`, payload, {
        headers: authHeaders(),
      });
      const created = response.data.data;
      toastr.success('Success', 'Template created successfully');
      dispatch(TEMPLATE_CREATE_SUCCESS({ template: created }));
      if (cb) cb(created);
      return created;
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.details ||
        error.message ||
        'Failed to create template';
      toastr.error('Error Creating Template', msg);
      dispatch(TEMPLATE_CREATE_FAIL({ error: { message: msg } }));
      throw error;
    }
  };
};

export const updateTemplate = (templateData, cb) => {
  return async (dispatch) => {
    dispatch(TEMPLATE_UPDATE_INIT());
    try {
      const templateId = templateData.id;
      if (!templateId) throw new Error('Template ID is missing for update.');
      const siteId = templateData.siteId;
      if (!siteId) throw new Error('Site ID is missing for update.');
      const payload = {
        name: templateData.name,
        description: templateData.description,
        screenIds: templateData.screenIds || [],
        start: templateData.start,
        end: templateData.end,
        totalDuration: templateData.totalDuration || 60,
        isArchived: templateData.isArchived || false,
        slots: templateData.slots || [],
      };
      const response = await axios.put(
        `${url}/template/${siteId}/${templateId}`,
        payload,
        { headers: authHeaders() }
      );
      const updated = response.data.data;
      toastr.success('Success', 'Template updated successfully');
      dispatch(
        TEMPLATE_UPDATE_SUCCESS({ template: updated, id: templateId })
      );
      if (cb) cb(updated);
      return updated;
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.details ||
        error.message ||
        'Failed to update template';
      toastr.error('Error Updating Template', msg);
      dispatch(TEMPLATE_UPDATE_FAIL({ error: { message: msg } }));
      throw error;
    }
  };
};

export const deleteTemplate = ({ siteId, id }, cb) => {
  return async (dispatch) => {
    dispatch(TEMPLATE_DELETE_INIT());
    try {
      await axios({
        method: 'delete',
        url: `${url}/template/delete`,
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        data: { siteId, id },
      });
    } catch (error) {
      const msg = error?.response?.data?.message || 'Failed to delete template';
      toastr.error('', msg, { icon: 'error' });
      return dispatch(TEMPLATE_DELETE_FAIL({ error: msg }));
    }
    toastr.success('', 'The template was deleted.');
    if (cb) cb();
    return dispatch(TEMPLATE_DELETE_SUCCESS({ id }));
  };
};

export const setTemplate = (data, cb) => {
  return (dispatch) => {
    dispatch(SET_TEMPLATE({ data }));
    if (cb) cb();
  };
};

export const clearTemplatesData = () => (dispatch) =>
  dispatch(TEMPLATE_CLEAR_DATA());

export const clearTemplatesDataLogout = () => (dispatch) =>
  dispatch(TEMPLATE_CLEAR_DATA_LOGOUT());

export const templatesCleanUp = () => (dispatch) =>
  dispatch(TEMPLATE_CLEAN_UP());
